<template>
    <div class="module">
        <div class="container container--full container--unpadded container--nooverflow">
            <div class="workout-intro" :class="{'workout-intro--christmas' : christmasWorkout}" v-if="workout">
                <div class="workout-intro__media">
                    <div class="workout-intro__media__item">
                        <div class="workout-intro__media__item__image" @click="playVideo">
                            <div class="workout-intro__media__item__play" v-if="workout.introVideoUrl">
                                <Icon name="play-circle"></Icon>
                            </div>
                            <v-lazy-image :src="workout.coverImageUrl" alt="" />
                        </div>
                        <div class="workout-intro__media__item__video" :class="{'loaded': introVideoLoaded}" v-if="workout.introVideoUrl">
                            <video :src="workout.introVideoUrl" preload="none" ref="introVideo" muted playsinline></video>
                            <div class="workout-intro__media__item__togglesound" @click="toggleSound">
                                <Icon :name="introVideoMuted ? 'mute' : 'unmute'"></Icon>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="workout-intro__content">
                    <div class="workout-intro__content__top">
                        <h1 class="workout-intro__title h1">{{workout.title}}</h1>
                        <div class="workout-intro__subtitle subtitle">{{workout.subtitle}}</div>

                        <div class="workout-intro__favourite" v-if="isWorkout && !christmasWorkout">
                            <FavouriteButton :workoutId="workout.id" :favourited="workout.favourited"></FavouriteButton>
                        </div>
                    </div>

                    <div class="workout-intro__content__main">
                        <div class="workout-intro__content__main__left">
                            <div class="workout-intro__info" v-if="isWorkout">
                                <div class="workout-intro__info__left">
                                    <div class="workout-intro__info__duration">
                                        <div class="duration">{{formatDuration(workout.duration)}}</div>
                                        <div v-translate>min_abbr</div>
                                    </div>
                                    <div class="workout-intro__info__trainer">
                                        <div class="difficulty">{{workout.difficulty}}</div>
                                        <div class="trainer"><translate>train_with</translate> <strong>{{workout.trainer.name}}</strong></div>
                                    </div>
                                </div>
                                <!-- <div class="workout-intro__info__right">
                                    <div class="workout-intro__info__label">Lower Body</div>
                                </div> -->
                            </div>

                            <div class="workout-intro__info" v-if="workout.startTime">
                                <div class="workout-intro__info__left">
                                    <div class="workout-intro__info__label">
                                        <translate>when</translate>
                                    </div>
                                    <div class="workout-intro__info__date text">
                                        <div class="day">{{formatDate(workout.startTime)}}</div>
                                        <div class="time">{{formatTime(workout.startTime)}}</div>
                                    </div>
                                </div>
                                <div class="workout-intro__info__right" v-if="workout.location">
                                    <div class="workout-intro__info__label"><translate>where</translate></div>
                                    <a 
                                        :href="`https://www.google.com/maps/search/?api=1&query=${workout.coordinates.lat},${workout.coordinates.lng}`" 
                                        target="_blank"
                                        class="workout-intro__info__location text" 
                                        v-if="workout.coordinates && workout.coordinates.lat">{{workout.location}}</a>
                                    <div class="workout-intro__info__location text" v-else>{{workout.location}}</div>
                                </div>
                            </div>

                            <div class="workout-intro__description text text--large" v-if="workout.description">
                                {{workout.description}}
                            </div>
                            <div class="workout-intro__kcal text" v-if="isWorkout">
                                <translate>estimated</translate> <strong>{{kcal}} <translate>kcal</translate></strong>
                            </div>
                            <div class="workout-intro__interests" v-if="isWorkout">
                                <div class="workout-intro__interests__title h6" v-translate>interested_in</div>
                                <div class="workout-intro__interests__text text">
                                    {{interests}}
                                </div>
                            </div>
                        </div>

                        <div class="workout-intro__content__main__right" v-if="isWorkout">
                            <div class="workout-intro__overview">
                                <div class="workout-intro__overview__title h6" v-translate>overview</div>
                                <div class="workout-intro__overview__sections">
                                    <div class="workout-intro__overview__section" v-for="section in sortedSections" :key="section.id"  :class="section.kind">
                                        <div class="workout-intro__overview__section__icon">
                                            <img :src="require(`@/assets/icons/${section.kind}_white.svg`)" alt="" v-if="christmasWorkout"/>
                                            <img :src="require(`@/assets/icons/${section.kind}.svg`)" alt="" v-else />
                                        </div>
                                        <div class="workout-intro__overview__section__line"></div>
                                        <div class="workout-intro__overview__section__text">
                                            <div class="kind text text--large">{{formatSectionKind(section.kind)}}</div>
                                            <div class="duration copy-label">{{formatDuration(section.duration)}} <translate>min_abbr</translate></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="workout-intro__content__bottom">
                        <div class="workout-intro__section" v-for="section in sortedSections" :key="section.id" v-if="sortedSections">
                            <div class="workout-intro__section__title h6">{{formatSectionKind(section.kind)}}</div>
                            <div class="workout-intro__section__exercises">
                                <div class="workout-intro__section__exercise" v-for="exercise in section.exercises" :key="exercise.id">
                                    <div class="workout-intro__section__exercise__image">
                                        <v-lazy-image :src="exercise.imageUrl" alt="" />
                                    </div>
                                    <div class="workout-intro__section__exercise__text">
                                        <div class="text text--large">{{exercise.title}}</div>
                                        <div class="copy-label">{{exercise.amount}} <translate>sec_abbr</translate></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="workout-intro__contentblock" v-if="sortedContent && !eventButtonBottom" v-for="content in sortedContent" :key="content.id">
                            <div class="workout-intro__contentblock__title h4" v-if="content.title">{{content.title}}</div>
                            <!-- text -->
                            <div class="workout-intro__contentblock__text text" v-if="content.type === 'TextBlock'" v-html="content.text"></div>

                            <!-- image -->
                            <div class="workout-intro__contentblock__image" v-if="content.type === 'ImageBlock'">
                                <v-lazy-image :src="content.fileUrl"></v-lazy-image>
                            </div>

                            <!-- video -->
                            <div class="workout-intro__contentblock__video" v-if="content.type === 'VideoBlock'">
                                <video controls :src="content.fileUrl"></video>
                            </div>

                            <!-- audio -->
                            <div class="workout-intro__contentblock__audio" v-if="content.type === 'AudioBlock'">
                                <audio controls :src="content.fileUrl"></audio>
                            </div>

                            <!-- button -->
                            <div class="workout-intro__contentblock__button" v-if="content.type === 'ButtonBlock'">
                                <puma-button :link="content.text" target="_blank">{{content.label}}</puma-button>
                            </div>
                        </div>

                        <!-- got it button -->
                        <div class="workout-intro__button" v-if="!isWorkout">
                            <puma-button :link="'/'"><translate>got_it</translate></puma-button>
                        </div>
                    </div>


                    <router-link :to="{ name: 'WorkoutProgress', params: { id: workout.id } }" class="workout-intro__start" v-if="isWorkout">
                        <translate>start_workout</translate>
                    </router-link>

                    <a v-if="eventButtonBottom" :href="sortedContent[0].text" target="_blank" class="workout-intro__start">
                        {{sortedContent[0].label}}
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex"
import FavouriteButton from '@/components/FavouriteButton'

export default {
    components: {
        FavouriteButton
    },
    props: {
        workout: {
            type: [Boolean, Object],
            default: false,
        },
        christmasWorkout: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            sortedSections: false,
            sortedContent: false,
            introVideoLoaded: false,
            introVideoMuted: true,
            monthNames: [
                this.$gettext('january_short'),
                this.$gettext('february_short'),
                this.$gettext('march_short'),
                this.$gettext('april_short'),
                this.$gettext('may_short'),
                this.$gettext('june_short'),
                this.$gettext('july_short'),
                this.$gettext('august_short'),
                this.$gettext('september_short'),
                this.$gettext('october_short'),
                this.$gettext('november_short'),
                this.$gettext('december_short')
            ],
        }
    },
    mounted() {
        this.sortSections()
        this.loadIntroVideo()
    },
    computed: {
        ...mapGetters(['getWeight', 'getMET']),
        isWorkout() {
            return this.$route.name === 'WorkoutOverview'
        },
        kcal() {
            let kcal = 0;
            if(this.sortedSections) {
                this.sortedSections.forEach(section => {
                    kcal += this.getMET(section, this.workout.difficulty) * this.getWeight * (section.duration / 3600)
                })
            }
            return Math.round(kcal)
        },
        interests() {
            let interestString = ''
            for (let i = 0; i < this.workout.interests.length; i++) {
                interestString += this.workout.interests[i].name
                if(i + 1 < this.workout.interests.length) {
                    interestString += ', '
                }
            }
            return interestString
        },
        eventButtonBottom() {
            return !this.isWorkout && this.sortedContent.length === 1 && this.sortedContent[0].type === 'ButtonBlock'
        }
    },
    methods: {
        sortSections() {
            if(this.isWorkout) {
                this.sortedSections = this.workout.sections.sort((a, b) => (a.order > b.order) ? 1 : -1);
                this.sortedSections.forEach(section => {
                    section.exercises = section.exercises.sort((a, b) => (a.order > b.order) ? 1 : -1);
                })
            } else if(this.workout.contentBlocks) {
                this.sortedContent = this.workout.contentBlocks.sort((a, b) => (a.order > b.order) ? 1 : -1);
            }
        },
        loadIntroVideo() {
            setTimeout(() => {
                if(this.$refs.introVideo) {
                    this.$refs.introVideo.load()

                    this.$refs.introVideo.addEventListener('loadeddata', () => {
                        this.introVideoLoaded = true
                        this.$refs.introVideo.play()
                    });

                    this.$refs.introVideo.addEventListener('ended', () => {
                        this.introVideoLoaded = false
                    });  
                }
            }, 300);
        },
        formatDuration(time) {
            return Math.round(time / 60);
        },
        formatSectionKind(kind) {
            return kind.split('_').join(' ')
        },
        playVideo() {
            if(this.workout.introVideoUrl) {
                this.introVideoLoaded = true
                this.$refs.introVideo.muted = false
                this.introVideoMuted = false

                this.$refs.introVideo.play()

                this.$refs.introVideo.addEventListener('ended', () => {
                    this.introVideoLoaded = false
                });
            }
        },
        toggleSound() {
            this.introVideoMuted = !this.introVideoMuted
            this.$refs.introVideo.muted = this.introVideoMuted 
        },
        formatDate(date) {
            const formattedDate = new Date(date)
            return `${this.monthNames[formattedDate.getMonth()]} ${formattedDate.getDate()}, ${formattedDate.getFullYear()}`
        },
        formatTime(date) {
            const formattedDate = new Date(date)
            return `${this.padNumber(formattedDate.getHours())}:${this.padNumber(formattedDate.getMinutes())}`
        },
        padNumber (number) {
            if (number < 10) {
                return `0${number}`;
            } else {
                return number;
            }
        },
    }
}
</script>

<style scoped lang="scss">
@import "~sass-mq";

.workout-intro {
    $block: &;

    display: flex;

    @include mq($until: tablet-landscape) {
        flex-direction: column;
    }

    &--christmas {
        background-color: $puma-color-black;
        color: $puma-color-white;

        #{$block} {

            &__title {
                color: $puma-color-accent-40;
            }

            &__info {
                &__duration {
                    color: $puma-color-accent-40;
                    border-right-color: $puma-color-accent-40;
                }
            }

            &__overview {

                &__section {

                    &__line {
                        &:after {
                            border-color: $puma-color-black !important;
                        }

                        &:before {
                            border-color: $puma-color-white !important; 
                        }

                        &:nth-child(2n) {
                            
                            &:after {
                                background: $puma-color-accent-40;
                            }
                        }

                    }

                    &:nth-child(2n) {
                        &.circuit,
                        &.run {
                            #{$block}__overview__section__line {
                                &:after {
                                    background: $puma-color-accent-60;
                                }
                            }
                        }
                    }
                }
            }
            
            &__start{
                background-color: $puma-color-white;
                color: $puma-color-black;
                height: 84px;
                line-height: 84px;
            }
        }
    }

    &__media {
        width: (5 * 100% / 12);

        @include mq($until: tablet-landscape) {
            width: 100%;
        }

        &__item {
            position: sticky;
            top: $nav-height-desktop;
            height: calc(100vh - #{$nav-height-desktop});

            @include mq($until: tablet-landscape) {
                position: relative;
                top: 0;
                height: auto;

                padding-bottom: 75%;
            }

            @include mq($until: mobile-landscape) {
                padding-bottom: 120%;
            }

            &__image {
                @include fillout;
                cursor: pointer;


                img {
                    @include object-fit;
                }
            }

            &__play {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                z-index: 1;

                .icon {
                    color: white;
                    width: 80px;
                    height: 80px;
                }
            }

            &__video {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                overflow: hidden;
                z-index: 3;

                pointer-events: none;
                opacity: 0;
                transition: opacity 300ms $ease-in-quad;

                @include mq($until: tablet-landscape) {
                    height: 100%;
                }

                &.loaded {
                    opacity: 1;
                    pointer-events: auto;
                }

                video {
                    @include object-fit;

                    @supports (-ms-ime-align: auto) {
                        height: auto;
                    }
                }
            }

            &__togglesound {
                position: absolute;
                bottom: 20px;
                right: 20px;
                width: 30px;
                height: 30px;
                border-radius: 100%;
                background: rgba(black, 0.4);
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;

                .icon {
                    color: white;
                    width: 26px;
                    height: 26px;
                }
            }
        }
    }

    &__content {
        position: relative;
        width: (7 * 100% / 12);
        display: flex;
        flex-direction: column;

        @include mq($until: tablet-landscape) {
            width: 100%;
        }

        &__top {
            position: relative;
            padding: 60px 80px 40px 40px;
            border-bottom: 1px solid rgba(black, 0.1);

            @include mq($until: desktop) {
                padding: 30px 60px $page-padding-small $page-padding-small;
            }
        }

        &__main {
            display: flex;
            border-bottom: 1px solid rgba(black, 0.1);

            @include mq($until: tablet) {
                flex-direction: column;
            }

            &__left {
                width: (4.8 * 100% / 7);
                padding: 40px;
                border-right: 1px solid rgba(black, 0.1);

                @include mq($until: desktop) {
                    padding: $page-padding-small;
                }

                @include mq($until: tablet) {
                    width: 100%;
                }

                &:only-child {
                    border: none;
                }

                &:empty {
                    display: none;
                }
            }

            &__right {
                width: (2.2 * 100% / 7);
                padding: 40px 40px 80px;

                @include mq($until: desktop) {
                    padding: $page-padding-small $page-padding-small 40px;
                }

                @include mq($until: tablet) {
                    width: 100%;
                }
            }   
        }

        &__bottom {
            padding: 60px 40px;
            flex-grow: 1;

            @include mq($until: desktop) {
                padding: 40px $page-padding-small;
            }
        }
    }

    &__info {
        display: flex;
        margin-bottom: 40px;

        & > * {
            max-width: 50%;
        }

        &__left {
            display: flex;
            // flex-wrap: wrap;
        }

        &__right {
            margin-left: auto;
        }

        &__duration {
            padding-right: 20px;
            border-right: 1px solid rgba(black, 0.1);

            .duration {
                font-size: $puma-size-font-2xl;
                font-weight: $font-weight-bold;
                line-height: 1.25;
                margin-bottom: -5px;
            }
        }

        &__trainer {
            padding-left: 20px;

            .difficulty {
                text-transform: capitalize;
            }
        }

        &__label {
            font-size: $puma-size-font-xs;
            text-transform: uppercase;
        }

        &__date {
            width: 100%;
            font-weight: $font-weight-bold;
            text-transform: uppercase;

            .time {
                font-size: $puma-size-font-2xl;
                line-height: 1;
            }
        }

        &__location {
            display: block;
        }
    }

    &__kcal {
        margin-top: 12px;
    }

    &__interests {
        margin-top: 60px;

        &__title {
            text-transform: uppercase;
            margin-bottom: 20px;

            @include mq($until: tablet) {
                margin-bottom: 4px;
            }
        }
    }

    &__overview {
        &__title {
            text-transform: uppercase;
            margin-bottom: 20px;
        }

        &__section {
            display: flex;

            &:first-child {
                #{$block}__overview__section__text {
                    justify-content: flex-start;
                }
            }

            &:last-child {
                #{$block}__overview__section__text {
                    justify-content: flex-end;
                }
            }

            &:not(:last-child) {
                #{$block}__overview__section__text {
                    border-bottom: 1px solid rgba(black, 0.1);
                }
            }

            &:nth-child(2n) {
                &.circuit,
                &.run {
                    #{$block}__overview__section__line {
                       &:after {
                            background: $puma-color-neutral-80;
                       }
                    }
                }
            }

            &.circuit + .circuit,
            &.run + .run,
            &.circuit + .run,
            &.run + .circuit {
                #{$block}__overview__section__line {
                    margin-top: -5px;
                    height: 85px;
                }
            }

            &.warm_up + .warm_up {
                #{$block}__overview__section__line {
                    &:before {
                        display: none;
                    }

                    &:after {
                        height: 100%;
                    }
                }
            }

            &__icon {
                display: flex;
                align-items: center;
                margin-right: 20px;
            }

            &__line {
                width: 11px;
                height: 80px;
                flex-shrink: 0;

                .warm_up &,
                .cool_down & {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: flex-end;

                    &:before {
                        content: '';
                        display: block;
                        width: 7px;
                        height: 7px;
                        border: 1px solid $puma-color-neutral-80;
                        border-radius: 100%;
                    }

                    &:after {
                        content: '';
                        display: block;
                        width: 1px;
                        height: 40px;
                        border-left: 1px dashed $puma-color-neutral-80;
                    }
                }

                .cool_down & {
                    flex-direction: column-reverse;
                }

                .circuit &,
                .run & {
                    border-radius: 500px;
                    background: white;

                    &:after {
                        content: '';
                        display: block;
                        width: 100%;
                        height: 100%;
                        border-radius: 500px;
                        border: solid 2px $puma-color-neutral-10;
                        background-color: $puma-color-neutral-70;
                    }
                }
            }

            &__text {
                display: flex;
                flex-direction: column;
                justify-content: center;
                margin-left: 24px;

                .kind {
                    text-transform: capitalize;
                }
            }
        }
    }

    &__section {
        display: flex;

        @include mq($until: tablet) {
            flex-direction: column;
        }

        &:not(:last-child) {
            margin-bottom: 60px;

            @include mq($until: tablet) {
                margin-bottom: 40px;
            }
        }

        &__title {
            text-transform: uppercase;
            width: (2 * 100% / 7);
            flex-shrink: 0;

            @include mq($until: tablet) {
                width: 100%;
                margin-bottom: 12px;
            }
        }

        &__exercise {
            display: flex;
            align-items: center;

            &:not(:last-child) {
                margin-bottom: 8px;

                @include mq($until: tablet) {
                    margin-bottom: 4px;
                }
            }

            &__image {
                width: 102px;
                line-height: 0;
                flex-shrink: 0;

                @include mq($until: tablet) {
                    width: 80px;
                }

                img {
                    width: 100%;
                }
            }

            &__text {
                padding: 20px 24px;
            }
        }
    }

    &__start {
        position: sticky;
        left: 0;
        bottom: 0;
        height: 80px;
        width: 100%;
        display: block;
        background: black; 
        font-size: $puma-size-font-3xl;
        font-weight: $font-weight-bold;
        line-height: 80px;
        color: $puma-color-neutral-10;
        text-transform: uppercase;
        text-align: center;
        cursor: pointer;

        @include mq($until: tablet-landscape) {
            height: 60px;
            line-height: 60px;
            font-size: $puma-size-font-xl;
        }

        @include mq($until: mobile-landscape) {
            height: 50px;
            line-height: 50px;
            font-size: $puma-size-font-lg;
        }
    }

    &__favourite {
        position: absolute;
        bottom: 40px;
        right: 40px;

        @include mq($until: desktop) {
            right: 20px;
            bottom: 20px;
        }
    }

    &__contentblock {
        max-width: 33em;

        &:not(:last-child) {
            margin-bottom: 60px;

            @include mq($until: tablet) {
                margin-bottom: 40px;
            }
        }

        &__title {
            text-transform: uppercase;
            margin-bottom: 12px;
        }

        &__image {
            img {
                max-width: 100%;
            }
        }

        &__video {
            video {
                max-width: 100%;
            }
        }
    }
}

</style>